/**
 * WebSocket hook for real-time chat functionality
 * Note: WebSocket connections cannot use httpOnly cookies directly,
 * so we use a temporary token fetch approach for the connection.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

// Use window.location.origin to ensure WebSocket works across all deployment environments
const getOrigin = () => typeof window !== 'undefined' ? window.location.origin : '';
const getWsUrl = () => getOrigin().replace('https://', 'wss://').replace('http://', 'ws://');
const getApiUrl = () => getOrigin();

export const useChatWebSocket = (onMessage, onTyping, onStopTyping, onRead) => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(async () => {
    const WS_URL = getWsUrl();
    const API_URL = getApiUrl();
    
    if (!WS_URL) {
      return;
    }

    try {
      // Fetch a short-lived token for WebSocket connection
      // The backend will validate our httpOnly cookie and return a token
      const tokenResponse = await axios.get(`${API_URL}/api/auth/ws-token`, {
        withCredentials: true
      });
      const wsToken = tokenResponse.data.ws_token;
      
      if (!wsToken) {
        setConnectionError('Not authenticated');
        return;
      }

      const wsUrl = `${WS_URL}/api/ws/chat?token=${wsToken}`;
      
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'connected':
              // User authenticated
              break;
            case 'new_message':
              if (onMessage) onMessage(data);
              break;
            case 'user_typing':
              if (onTyping) onTyping(data);
              break;
            case 'user_stop_typing':
              if (onStopTyping) onStopTyping(data);
              break;
            case 'messages_read':
              if (onRead) onRead(data);
              break;
            case 'subscribed':
              // Subscribed to conversation
              break;
            case 'error':
              setConnectionError(data.message);
              break;
            default:
              // Unknown message type
              break;
          }
        } catch (e) {
          // Error parsing WebSocket message
        }
      };

      ws.current.onclose = (event) => {
        setIsConnected(false);
        
        // Attempt to reconnect if not a deliberate close
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeout.current = setTimeout(connect, delay);
        }
      };

      ws.current.onerror = () => {
        setConnectionError('Failed to connect to chat server');
      };
    } catch (error) {
      // If token fetch fails, user is likely not authenticated
      if (error.response?.status === 401) {
        setConnectionError('Not authenticated');
      } else {
        setConnectionError('Failed to initialize chat connection');
      }
    }
  }, [onMessage, onTyping, onStopTyping, onRead]);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    if (ws.current) {
      ws.current.close(1000, 'User disconnected');
      ws.current = null;
    }
    setIsConnected(false);
  }, []);

  const subscribe = useCallback((conversationId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'subscribe',
        conversation_id: conversationId
      }));
    }
  }, []);

  const unsubscribe = useCallback((conversationId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'unsubscribe',
        conversation_id: conversationId
      }));
    }
  }, []);

  const sendMessage = useCallback((conversationId, content) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'message',
        conversation_id: conversationId,
        content: content
      }));
      return true;
    }
    return false;
  }, []);

  const sendTyping = useCallback((conversationId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: conversationId
      }));
    }
  }, []);

  const sendStopTyping = useCallback((conversationId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'stop_typing',
        conversation_id: conversationId
      }));
    }
  }, []);

  const markAsRead = useCallback((conversationId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'mark_read',
        conversation_id: conversationId
      }));
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionError,
    subscribe,
    unsubscribe,
    sendMessage,
    sendTyping,
    sendStopTyping,
    markAsRead,
    reconnect: connect
  };
};

export default useChatWebSocket;
